/**
 * Meta Pixel Analytics Utility
 * 
 * Centralised, consent-aware wrapper around the Meta (Facebook) Pixel.
 * The pixel is loaded lazily – only after the user grants marketing consent.
 * 
 * Usage:
 *   import { initMetaPixel, trackPageView, trackStandardEvent, trackCustomEvent } from '@/lib/analytics/metaPixel';
 *   initMetaPixel();          // call once after consent is granted
 *   trackPageView();          // on each SPA route change
 *   trackStandardEvent('Lead', { value: 10 });
 *   trackCustomEvent('DemoViewed');
 */

import { META_PIXEL_ID } from './config';
import { getAdvancedMatchingParams } from './userData';

const PIXEL_ID = META_PIXEL_ID;

let initialised = false;

/** Type-safe fbq reference */
function getFbq(): ((...args: unknown[]) => void) | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return typeof window !== 'undefined' ? (window as any).fbq ?? null : null;
}

/**
 * Inject the Meta Pixel base script into the page and fire the initial `init` event.
 * PageView is fired separately by AnalyticsProvider via trackPageView() from events.ts,
 * which ensures Pixel + CAPI both fire with a shared event_id.
 *
 * Safe to call multiple times – subsequent calls are no-ops.
 */
export function initMetaPixel(): void {
  if (initialised) return;
  if (!PIXEL_ID) {
    console.warn('[MetaPixel] VITE_META_PIXEL_ID not set – skipping init.');
    return;
  }
  if (typeof window === 'undefined') return;

  try {
    // Inject Facebook Pixel base code (minified version from Meta docs)
    /* eslint-disable */
    (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        // @ts-ignore
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */

    const fbq = getFbq();
    if (fbq) {
      const matchParams = getAdvancedMatchingParams();
      if (matchParams) {
        fbq('init', PIXEL_ID, matchParams);
      } else {
        fbq('init', PIXEL_ID);
      }
    }

    initialised = true;
  } catch (err) {
    console.error('[MetaPixel] Failed to initialise:', err);
  }
}

/**
 * Track a standard PageView event.
 * Optionally accepts an event_id for CAPI deduplication.
 */
export function trackPageView(eventId?: string): void {
  const fbq = getFbq();
  if (!fbq || !initialised) return;
  try {
    fbq('track', 'PageView', {}, { eventID: eventId });
  } catch { /* swallow */ }
}

/**
 * Fire a Meta standard event (e.g. 'Lead', 'CompleteRegistration', 'Purchase').
 * Accepts an optional eventId for CAPI deduplication.
 * @see https://developers.facebook.com/docs/meta-pixel/reference#standard-events
 */
export function trackStandardEvent(
  eventName: string,
  params?: Record<string, unknown>,
  eventId?: string,
): void {
  const fbq = getFbq();
  if (!fbq || !initialised) return;
  try {
    fbq('track', eventName, params, { eventID: eventId });
  } catch { /* swallow */ }
}

/**
 * Fire a custom event (non-standard).
 * Accepts an optional eventId for CAPI deduplication.
 */
export function trackCustomEvent(
  eventName: string,
  params?: Record<string, unknown>,
  eventId?: string,
): void {
  const fbq = getFbq();
  if (!fbq || !initialised) return;
  try {
    fbq('trackCustom', eventName, params, { eventID: eventId });
  } catch { /* swallow */ }
}

/** Check whether the pixel has been initialised */
export function isPixelInitialised(): boolean {
  return initialised;
}
